import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDownIcon } from "@heroicons/react/24/solid";

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const navigate = useNavigate();

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const handleBack = () => {
    navigate(-1); 
  };

  const faqs = [
    {
      title: "What is the Selector field and how should I choose it?",
      icon: "🎯",
      content: (
        <>
          <p>
            The <strong className="text-emerald-200">Selector</strong> defines which part of your page or component the experiment targets. It typically accepts a CSS selector (e.g., <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">#signup-button</code>, <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">.header-banner</code>).
          </p>
          <ul className="mt-4 space-y-2 pl-6 list-disc">
            <li>Right-click the element you want to target and choose <strong className="text-emerald-200">Inspect</strong>.</li>
            <li>Find a unique <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">id</code> or <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">class</code> on the highlighted element.</li>
            <li>Use <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">#</code> for IDs and <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">.</code> for classes (e.g., <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">#signup-button</code>).</li>
          </ul>
        </>
      ),
    },
    {
      title: "What does the GoalSelector do and how do I configure it?",
      icon: "🎁",
      content: (
        <>
          <p>
            The <strong className="text-emerald-200">GoalSelector</strong> identifies the element that represents a conversion (e.g., a purchase button or form submit).
          </p>
          <ul className="mt-4 space-y-2 pl-6 list-disc">
            <li>Inspect the element you want to track.</li>
            <li>Identify its unique <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">id</code> or <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">class</code>.</li>
            <li>Use the appropriate prefix (e.g., <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">#purchase-button</code>, <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">.signup-confirmation</code>).</li>
          </ul>
        </>
      ),
    },
    {
      title: 'What should I do after clicking on "Create Experiment"?',
      icon: "⚙️",
      content: (
        <>
          <p>After creating the experiment, you'll receive a snippet to add inside your webpage's <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">&lt;head&gt;</code>.</p>
          <ol className="mt-4 space-y-2 pl-6 list-decimal">
            <li>Copy the generated snippet.</li>
            <li>Paste it inside the <code className="bg-black/30 px-2 py-1 rounded text-emerald-300">&lt;head&gt;</code> tag.</li>
            <li>Add the below helper snippet also to your site before the generated snippet. This helper snippet needs to be altered as per your test requirement using ChatGPT.</li>
          </ol>
          <pre className="bg-black/40 text-emerald-100 p-4 rounded-lg mt-4 overflow-x-auto text-sm border border-white/10 font-mono">{`<script>
  window.ABTestReady = function (variant, config) {
    const el = document.querySelector(config.selector);
    if (!el) return;

    el.style.backgroundColor = '';
    el.style.color = '';

    if (variant === 'A') {
      el.style.backgroundColor = 'green';
      el.style.color = 'white';
    } else {
      el.style.backgroundColor = 'red';
      el.style.color = 'white';
    }
  };
</script>`}
          </pre>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#f3f4f6] text-slate-900 py-16 px-6">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(67,179,126,0.08),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.05),_transparent_35%)] pointer-events-none" />
      
      <div className="relative max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="text-5xl font-black text-slate-900">❓ FAQ</h1>
            <p className="text-slate-600 mt-3 text-lg">
              Everything you need to know about setting up experiments
            </p>
          </div>
          <button
            onClick={handleBack}
            className="px-6 py-3 rounded-lg border border-slate-300 bg-white text-slate-800 font-semibold hover:bg-slate-100 hover:border-slate-400 transition"
          >
            ← Back
          </button>
        </div>

        {/* Accordion List */}
        <div className="space-y-4">
          {faqs.map((faq, idx) => {
            const isOpen = activeIndex === idx;
            return (
              <div
                key={idx}
                className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? "border-emerald-400/50 bg-[#173a32] shadow-lg shadow-emerald-500/10"
                    : "border-white/10 bg-[#173a32]/50 hover:border-white/20"
                }`}
              >
                <button
                  onClick={() => toggleAccordion(idx)}
                  className="w-full px-6 py-5 flex justify-between items-center hover:bg-white/5 transition"
                >
                  <span className="flex items-center gap-3 text-left">
                    <span className="text-2xl">{faq.icon}</span>
                    <span className={`text-lg font-bold ${isOpen ? "text-emerald-200" : "text-slate-100"}`}>
                      {faq.title}
                    </span>
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    className={`${isOpen ? "text-emerald-400" : "text-slate-400"}`}
                  >
                    <ChevronDownIcon className="w-6 h-6" />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-6 pb-6 border-t border-white/10 pt-6 text-slate-200 text-base leading-relaxed space-y-3">
                        {faq.content}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="mt-16 rounded-2xl border border-white/10 bg-[#173a32] p-8">
          <h3 className="text-2xl font-bold text-emerald-200 mb-4">� Ready to get started?</h3>
          <p className="text-slate-300 mb-6">
            Create your first experiment now and start running A/B tests. All the tools you need are just a few clicks away.
          </p>
          <button
            onClick={handleBack}
            className="px-6 py-3 rounded-lg bg-[#8ff0b3] text-[#0f2b24] font-bold hover:bg-[#b3f7ca] transition shadow-lg shadow-emerald-500/20"
          >
            Create Experiment
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
